import type { CityConfig } from "../city-template/types";

export const middlesboroughConfig: CityConfig = {
  city: "Middlesborough",
  state: "Kentucky",
  stateAbbr: "KY",
  slug: "middlesborough",
  route: "/middlesborough",
  title: "Emergency Passport Middlesborough, KY — Expedited & Fast Passport Service | Fast Passport Center",
  metaDescription: "Emergency passport and expedited passport services in Middlesborough, KY — as fast as 24 hours. Fast passport renewal and child passport expediting for Bell County and southeastern Kentucky travelers. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency \u0026 Expedited Passport Services in Middlesborough, KY",
  heroSubtitle: "Passport expired before your trip? Need an emergency passport fast? We help Middlesborough and southeastern Kentucky travelers get their expedited passports as quickly as 24 hours. Serving Pineville, Harlan, and the tri-state area. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Middlesborough Kentucky southeastern Kentucky cityscape at golden hour with Cumberland Gap mountains in the background, small town charm along the Clinch River, rolling hills of Appalachian Kentucky, historic downtown buildings with warm sunlight, clean modern cityscape photography, lush green landscape, no visible text or people, wide panoramic view\u0026width=1800\u0026height=700\u0026seq=middlesborough-hero-001\u0026orientation=landscape",
  heroImageAlt: "Middlesborough Kentucky with Appalachian Cumberland Gap mountains and southeastern Kentucky rolling hills",
  geoPosition: "36.6084;-83.7166",
  geoPlacename: "Middlesborough, Kentucky",
  regionalAgency: {
    name: "Atlanta Passport Agency",
    address: "230 Peachtree St NW, Suite 1000, Atlanta, GA 30308",
    distance: "Located in downtown Atlanta — approximately 320 miles from Middlesborough via I-75",
  },
  nearbyCitiesZips: [
    { name: "Middlesborough", zips: "40965" },
    { name: "Pineville", zips: "40977" },
    { name: "Harlan", zips: "40831" },
    { name: "Barbourville", zips: "40906" },
    { name: "Corbin", zips: "40701, 40702" },
    { name: "London", zips: "40741, 40742, 40743, 40744, 40745, 40746, 40747, 40748, 40749, 40750" },
    { name: "Jellico (TN)", zips: "37762" },
    { name: "Tazewell (TN)", zips: "37879" },
    { name: "Cumberland Gap (TN)", zips: "37724" },
    { name: "Ewing (VA)", zips: "24248" },
    { name: "New Tazewell (TN)", zips: "37824" },
    { name: "LaFollette (TN)", zips: "37766" },
    { name: "Morristown (TN)", zips: "37813, 37814, 37815, 37816" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent southeastern Kentucky departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels to our processing center" },
  ],
  internalLinks: [
    { name: "Expedited passport services in London", route: "/london" },
    { name: "Fast passport renewal in Corbin", route: "/corbin" },
    { name: "Emergency passport help in Harlan", route: "/harlan" },
    { name: "Urgent passport services in Somerset", route: "/somerset" },
    { name: "Same-day passport help in Knoxville", route: "/cities/knoxville" },
    { name: "Passport courier services in Pikeville", route: "/pikeville" },
  ],
  fpcOfficeAddress: "500 W Jefferson St, Louisville, KY 40202",
  fpcOfficeNote: "Fast Passport Center serves Middlesborough remotely via secure courier. We do not have a walk-in office in Middlesborough — our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for Bell County travelers.",
  fpcOfficeDistance: "Serving all of Bell County and southeastern Kentucky",
  serviceAreaIntro: "From downtown Middlesborough to the Pineville Pike, from Harlan to the Tennessee border — Fast Passport Center serves the entire Middlesborough community and southeastern Kentucky region. We regularly help travelers from Pineville, Harlan, and Corbin get their passports fast.",
  nearbyCitiesIntro: "We serve Middlesborough and all surrounding southeastern Kentucky and tri-state area cities including Pineville, Harlan, Barbourville, Corbin, London, Jellico (TN), Tazewell (TN), Cumberland Gap (TN), Ewing (VA), New Tazewell (TN), LaFollette (TN), and Morristown (TN). If you are in Bell County, southeastern Kentucky, or the tri-state area, we have you covered.",
  howItWorksIntro: "Most Middlesborough travelers complete everything online and ship documents overnight. No need to drive to Atlanta or wait in line at the regional agency.",
  mistakesIntro: "Middlesborough and Bell County travelers often face long drives to the nearest regional passport agency. Here are the most common mistakes that cause delays.",
  testimonialsIntro: "Middlesborough and southeastern Kentucky travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Middlesborough? Here are the most common ones we hear from Bell County travelers.",
  statePageRoute: "/states/kentucky",
  statePageName: "Kentucky",
  dmvWebsite: "https://drive.ky.gov",
  dmvLabel: "Kentucky Transportation Cabinet — Official Website",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Predictable turnaround",
        "Ideal for upcoming trips",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service",
        "Good for planned travel",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "No courier access — standard mail only",
        "Workload & wait times shift with shutdowns and budget fights",
        "In-person clerk appointment required",
        "No document review — errors cause rejection",
        "No dedicated agent — you are on your own",
        "Updates only by physical mail letter",
        "No phone or email contact for your case",
        "Can't upgrade speed after submission",
        "Not suitable if travel is within 10 weeks",
      ],
      highlight: false,
      muted: true,
    },
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Appointment required — no walk-ins allowed",
        "Life-or-death emergencies only",
        "Bereavement or medical crisis",
        "Expect to wait all day or return next day",
      ],
      highlight: false,
      emergency: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For travelers with immediate departure needs — as fast as 24 hours for urgent Bell County international flights.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement with DS-64 form guidance and police report documentation.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
    { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
  ],
  testimonials: [
    {
      name: "Amanda R.",
      location: "Middlesborough, KY",
      rating: 5,
      text: "Had a last-minute emergency trip to care for my sister in Canada. Fast Passport Center processed my emergency passport in 48 hours and kept me informed every step. Lifesaver.",
      service: "Emergency Passport",
    },
    {
      name: "Robert M.",
      location: "Pineville, KY",
      rating: 5,
      text: "Renewed my passport for a Caribbean cruise. They did it in 5 days and the whole process was seamless from the mountains of Kentucky.",
      service: "Passport Renewal",
    },
    {
      name: "Catherine T.",
      location: "Harlan, KY",
      rating: 5,
      text: "My daughter needed a passport for a school trip to Europe. They walked me through every step, helped me find the closest facility, and got it done in 6 days.",
      service: "Child Passport",
    },
    {
      name: "Daniel L.",
      location: "Middlesborough, KY",
      rating: 5,
      text: "Lost my passport the day before a business trip. Their emergency service had a replacement in my hands in 36 hours. Amazing work.",
      service: "Lost Passport",
    },
  ],
  facilities: [
    {
      id: "middlesborough-1",
      name: "Middlesborough Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1350 E Cumberland Ave",
      city: "Middlesborough",
      state: "KY",
      zip: "40965",
      phone: "(606) 248-2511",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.3 miles from downtown Middlesborough",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Major facility for Bell County. Appointments required. Call ahead to confirm passport acceptance times.",
      icon: "ri-mail-line",
    },
    {
      id: "middlesborough-2",
      name: "Pineville Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "401 S Walnut St",
      city: "Pineville",
      state: "KY",
      zip: "40977",
      phone: "(606) 337-2511",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "2 miles from downtown Pineville",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves Bell County and Pineville area. Photo services available. Book ahead.",
      icon: "ri-mail-line",
    },
    {
      id: "middlesborough-3",
      name: "Corbin Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1100 E Cumberland Gap Pkwy",
      city: "Corbin",
      state: "KY",
      zip: "40701",
      phone: "(606) 528-2511",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.5 miles from downtown Corbin",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Whitley County and southern Bell County. Full passport services with appointment.",
      icon: "ri-mail-line",
    },
    {
      id: "middlesborough-4",
      name: "Harlan Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "201 W Central St",
      city: "Harlan",
      state: "KY",
      zip: "40831",
      phone: "(606) 573-2511",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.2 miles from downtown Harlan",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves Harlan County. Limited weekend hours.",
      icon: "ri-mail-line",
    },
    {
      id: "middlesborough-5",
      name: "London Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1000 W Hal Rogers Pkwy",
      city: "London",
      state: "KY",
      zip: "40741",
      phone: "(606) 864-2511",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.5 miles from downtown London",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Major facility for Laurel County. High volume — book appointments 2+ weeks in advance.",
      icon: "ri-mail-line",
    },
    {
      id: "middlesborough-6",
      name: "Morristown (TN) Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "3115 E Andrew Johnson Hwy",
      city: "Morristown",
      state: "TN",
      zip: "37814",
      phone: "(423) 581-2511",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.5 miles from downtown Morristown",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Major facility for Hamblen County. Full passport services with appointment.",
      icon: "ri-mail-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — 5.5 hour drive to Atlanta", fpc: "No — ship from home or office" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Southeastern Kentucky photo shops often use generic sizes. Passport photos must be exactly 2×2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 reasons for rejection at Bell County acceptance facilities.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Fees change frequently. Many Middlesborough applicants show up with incorrect checks and have to reschedule.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel",
      description: "Without flight confirmation or travel itinerary, the Atlanta regional agency will turn you away for emergency appointments.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For minors, both parents must appear or submit notarized consent. This trips up many Bell County families.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "Appalachian Kentucky travelers often assume standard processing will be fine. It takes 8–11 weeks. Start expedited at least 3 weeks before travel.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location in Middlesborough or Corbin." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Middlesborough address — trackable every step of the way." },
  ],
  faqs: [
    { q: "Can I get an emergency passport in Middlesborough before international travel?", a: "Yes. Fast Passport Center provides emergency passport services in Middlesborough for travelers with urgent international flights. Our registered couriers hand-deliver documents directly to U.S. Department of State facilities, achieving 24–48 hour turnaround — without the 5.5 hour drive to Atlanta." },
    { q: "How fast can I renew my passport in Middlesborough?", a: "Fast Passport Center offers fast passport renewal in Middlesborough in as little as 24–48 hours through our emergency courier service. For less urgent travel, our 3–5 day expedited option provides the best value." },
    { q: "Where are the closest passport acceptance facilities in Middlesborough?", a: "Popular options include the Middlesborough Post Office (40965), Pineville Post Office (40977), Corbin Post Office (40701), Harlan Post Office (40831), and London Post Office (40741). All require appointments. We help you find the most convenient location." },
    { q: "Can I expedite a child passport in Middlesborough?", a: "Yes. Fast Passport Center expedites child passports in Middlesborough for minors under 16. Both parents must appear or submit notarized consent, along with the child's birth certificate and both parents' IDs." },
    { q: "Do I need to drive to the Atlanta Passport Agency?", a: "No. With Fast Passport Center, you complete everything online and ship your documents overnight. Our couriers handle the in-person delivery to State Department facilities." },
    { q: "How much does expedited passport service cost in Middlesborough?", a: "Government fees are fixed: $130 for a passport book (adult) plus a $60 expedite fee. Fast Passport Center's service fees vary by processing speed. Contact us for a personalized quote." },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Middlesborough", address: "1500 E Cumberland Ave, Middlesborough, KY 40965", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walmart — Corbin", address: "50 S Stewart Rd, Corbin, KY 40701", hours: "Mon–Sun: 6AM–11PM" },
    { name: "Walgreens — London", address: "1750 S Main St, London, KY 40741", hours: "Mon–Sun: 7AM–11PM" },
    { name: "Rite Aid — Harlan", address: "100 S Main St, Harlan, KY 40831", hours: "Mon–Sun: 8AM–10PM" },
  ],
  dmvLocations: [
    {
      name: "Circuit Court Clerk — Bell County",
      address: "101 S Main St, Middlesborough, KY 40965",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "Driver's license and REAL ID. Appointment recommended.",
      featured: true,
    },
    {
      name: "Circuit Court Clerk — Whitley County",
      address: "200 S Main St, Corbin, KY 40701",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "REAL ID and driver's license renewals. Online booking available.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "TYS",
      name: "McGhee Tyson Airport (Knoxville)",
      distance: "70 miles from Middlesborough",
      address: "2055 Alcoa Hwy, Alcoa, TN 37701",
      note: "The closest major airport for southeastern Kentucky. Domestic flights with connections to international hubs.",
      flightUrl: "https://www.tys.org/",
      flightLabel: "Knoxville Airport",
      featured: true,
    },
    {
      code: "HTS",
      name: "Tri-State Airport (Huntington)",
      distance: "85 miles from Middlesborough",
      address: "1449 Airport Rd, Huntington, WV 25704",
      note: "Regional airport with connections to major hubs.",
      flightUrl: "https://www.tristateairport.com/",
      flightLabel: "Tri-State Airport",
      featured: false,
    },
  ],
  vitalRecordsIcon: "ri-heart-pulse-line",
  vitalRecordsLinks: [
    { icon: "ri-file-text-line", label: "Birth & Death Certificates", desc: "Kentucky Office of Vital Statistics in Frankfort — order certified copies online, by mail, or in person for passport applications and name changes.", url: "https://www.chfs.ky.gov/agencies/dph/dehp/vsb/Pages/default.aspx" },
    { icon: "ri-heart-line", label: "Marriage Records", desc: "Bell County Clerk — certified marriage licenses for name change passport applications and legal documentation.", url: "https://www.bellcounty.ky.gov/" },
    { icon: "ri-scales-line", label: "Divorce Decrees", desc: "Bell Circuit Court Clerk — certified divorce decrees supporting passport name change or correction.", url: "https://kycourts.gov/Courts/Circuit-Court/Pages/Bell.aspx" },
    { icon: "ri-global-line", label: "VitalChek — Nationwide Records", desc: "If your record was issued outside Kentucky, order certified copies online through VitalChek for delivery in 5–10 business days.", url: "https://www.vitalchek.com/" },
  ],
};